'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const supabase = createClient();
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const isMobile = /Mobi|Android/i.test(ua);
      const deviceType = isMobile ? 'Mobile' : 'Desktop';
      const browserName = /Chrome/i.test(ua) ? 'Chrome' : /Firefox/i.test(ua) ? 'Firefox' : /Safari/i.test(ua) ? 'Safari' : /Edge/i.test(ua) ? 'Edge' : 'Unknown';

      await supabase.from('printer_leads').insert({
        name: form.name,
        phone: form.phone || '',
        printer_model: form.subject,
        device_type: deviceType,
        browser: browserName,
      });
    } catch {
      // silently ignore — don't block UX
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <div>
      <Header />

      {/* Hero */}
      <div className="page-hero">
        <div className="page-hero-grid" />
        <div className="page-hero-glow" />
        <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(0,92,187,0.25)', border: '1px solid rgba(0,92,187,0.4)', color: '#70aeff', fontSize: 12, fontWeight: 600, letterSpacing: '1.5px', padding: '6px 14px', borderRadius: 99, textTransform: 'uppercase', marginBottom: '1.2rem' }}>
            <span style={{ width: 6, height: 6, background: '#70aeff', borderRadius: '50%', display: 'inline-block' }}></span>
            Get In Touch
          </div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 'clamp(2.2rem, 4vw, 3.4rem)', color: '#fff', lineHeight: 1.15, marginBottom: '0.75rem' }}>
            Contact <span style={{ color: '#FF6B00' }}>Us</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: 560 }}>Have a question about printers or need support? We&apos;re here to help!</p>
        </div>
      </div>

      <section style={{ padding: '64px 16px', background: '#f0f4ff' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48 }}>
          {/* Contact Info */}
          <div>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.8rem', color: '#1A2235', marginBottom: 32 }}>Get In Touch</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {[
                { icon: '📍', title: 'Our Location', lines: ['2338 Irving Blvd', 'Dallas, TX 75207', 'United States'] },
                { icon: '📞', title: 'Phone Number', lines: ['+1 (214) 963-8856'] },
                { icon: '📧', title: 'Email', lines: ['info@printmobhub.store'] },
                { icon: '🌐', title: 'Website', lines: ['printmobhub.store'] },
                { icon: '⏰', title: 'Business Hours', lines: ['Mon–Fri: 9:00 AM – 6:00 PM CST', 'Sat: 10:00 AM – 4:00 PM CST', 'Sun: Closed'] },
              ].map(item => (
                <div key={item.title} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: '#E8F1FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1A2235', marginBottom: 4 }}>{item.title}</div>
                    {item.lines.map(line => <div key={line} style={{ color: '#6B7A99', fontSize: 14, lineHeight: 1.6 }}>{line}</div>)}
                  </div>
                </div>
              ))}
            </div>

            {/* Map placeholder */}
            <div style={{ marginTop: 32, borderRadius: 16, overflow: 'hidden', border: '1px solid #E2E8F4' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3355.0!2d-96.8697!3d32.7767!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s2338+Irving+Blvd%2C+Dallas%2C+TX+75207!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="250"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                title="Printmob Hub Location"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div style={{ background: '#fff', borderRadius: 20, padding: 40, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #E2E8F4' }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#1A2235', marginBottom: 12 }}>Message Sent!</h3>
                <p style={{ color: '#6B7A99', lineHeight: 1.7 }}>Thank you for contacting Printmob Hub. We&apos;ll get back to you within 24 hours.</p>
                <button onClick={() => setSubmitted(false)} style={{ marginTop: 24, background: '#005CBB', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 32px', fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>Send Another Message</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#1A2235', marginBottom: 24 }}>Send Us a Message</h3>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Full Name *</label>
                      <input required type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="John Doe" style={{ width: '100%', border: '1px solid #E2E8F4', borderRadius: 8, padding: '10px 14px', fontSize: 14, outline: 'none', transition: '0.2s' }} onFocus={e => e.target.style.borderColor = '#005CBB'} onBlur={e => e.target.style.borderColor = '#E2E8F4'} />
                    </div>
                    <div>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Email *</label>
                      <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@example.com" style={{ width: '100%', border: '1px solid #E2E8F4', borderRadius: 8, padding: '10px 14px', fontSize: 14, outline: 'none', transition: '0.2s' }} onFocus={e => e.target.style.borderColor = '#005CBB'} onBlur={e => e.target.style.borderColor = '#E2E8F4'} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Phone</label>
                    <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+1 (214) 000-0000" style={{ width: '100%', border: '1px solid #E2E8F4', borderRadius: 8, padding: '10px 14px', fontSize: 14, outline: 'none', transition: '0.2s' }} onFocus={e => e.target.style.borderColor = '#005CBB'} onBlur={e => e.target.style.borderColor = '#E2E8F4'} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Subject *</label>
                    <select required value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} style={{ width: '100%', border: '1px solid #E2E8F4', borderRadius: 8, padding: '10px 14px', fontSize: 14, outline: 'none', background: '#fff', cursor: 'pointer' }}>
                      <option value="">Select a subject</option>
                      <option>Product Inquiry</option>
                      <option>Order Support</option>
                      <option>Technical Support</option>
                      <option>Returns & Refunds</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Message *</label>
                    <textarea required value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Tell us how we can help..." rows={5} style={{ width: '100%', border: '1px solid #E2E8F4', borderRadius: 8, padding: '10px 14px', fontSize: 14, outline: 'none', resize: 'none', transition: '0.2s' }} onFocus={e => e.target.style.borderColor = '#005CBB'} onBlur={e => e.target.style.borderColor = '#E2E8F4'} />
                  </div>
                  <button type="submit" disabled={submitting} style={{ background: '#005CBB', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 32px', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 15, transition: 'background 0.2s', opacity: submitting ? 0.7 : 1 }}
                    onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = '#003D7A'; }}
                    onMouseLeave={e => e.currentTarget.style.background = '#005CBB'}>
                    {submitting ? 'Sending...' : 'Send Message →'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
